import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { DialogHelper } from "@core/helpers/dialog-helper";
import { FinancialInstitute } from "@core/models/financial-insitute.model";
import {
  Intermediary,
  TIntermediariesPaginated,
} from "@core/models/intermediary.model";
import { ManagementService } from "@core/services/management.service";
import { NotificationService } from "@core/services/notification.service";
import { Utilities } from "@core/utils/utils";
import { TableSorting, TPagination } from "@custom-types/sorting";
import { ToasterSeverities } from "@enums/toaster-severities";
import {
  AccountManager,
  TAccountManagersPaginated,
} from "@models/account-manager.model";
import { Advisor, TAdvisorsPaginated } from "@models/advisor.model";
import { Country } from "@models/country.model";
import { Document, TDocumentsPaginated } from "@models/document.model";
import { Email, TEmailsPaginated } from "@models/email.model";
import { Etf } from "@models/etf-model";
import { Insurer, TInsurersPaginated } from "@models/insurer.model";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  concatMap,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from "rxjs";

import * as managementActions from "./management.actions";
import { SET_MANAGEMENT_LOADING_STATUS } from "./management.actions";
import {
  SELECT_DOCUMENTS_PAGINATION,
  SELECT_MANAGEMENT_PAGINATION,
} from "./management.selectors";

@Injectable()
export class ManagementEffects {
  loadAdvisors$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_ADVISORS),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .getAdvisors$(action.searchTerm, action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<Advisor> | TAdvisorsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_ADVISORS({
                        advisorList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_ADVISORS({ advisorList: response })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_ADVISORS({ advisorList: [] }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadInsurers$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_INSURERS),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .getInsurers$(action.searchTerm, action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<Insurer> | TInsurersPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_INSURERS({
                        insurerList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_INSURERS({ insurerList: response })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_INSURERS({ insurerList: [] }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_MANAGEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadAccountManagers$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_ACCOUNT_MANAGERS),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .getAccountManagers$(
              action.searchTerm,
              action.pagination ?? pagination
            )
            .pipe(
              switchMap(
                (
                  response: Array<AccountManager> | TAccountManagersPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                  );
                  if ("data" in response && "totalRecords" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.totalRecords,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_ACCOUNT_MANAGERS({
                        accountManagerList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_ACCOUNT_MANAGERS({
                      accountManagerList: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_ACCOUNT_MANAGERS({
                    accountManagerList: [],
                  }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_MANAGEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadFinancialInstitutions$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_FINANCIAL_INSTITUTIONS),
      mergeMap((action: { searchTerm?: string }): Observable<Action> => {
        this.store.dispatch(
          managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
            loading: true,
          })
        );
        return this.managementService
          .getFinancialInstitutions$(action.searchTerm)
          .pipe(
            switchMap(
              (
                financialInstituteList: Array<FinancialInstitute>
              ): Observable<Action> => {
                this.store.dispatch(
                  managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
                    loading: false,
                  })
                );
                return of(
                  managementActions.SET_FINANCIAL_INSTITUTIONS({
                    financialInstituteList,
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of(
                managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
                  loading: false,
                }),
                managementActions.SET_FINANCIAL_INSTITUTIONS({
                  financialInstituteList: [],
                })
              );
            })
          );
      })
    );
  });

  loadIntermediaries$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_INTERMEDIARIES),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .getIntermediaries$(
              action.searchTerm,
              action.pagination ?? pagination
            )
            .pipe(
              switchMap(
                (
                  response: Array<Intermediary> | TIntermediariesPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_INTERMEDIARIES({
                        intermediaryList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_INTERMEDIARIES({
                      intermediaryList: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_INTERMEDIARIES({
                    intermediaryList: [],
                  }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_MANAGEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  saveAdvisors$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_ADVISOR),
      mergeMap(
        (action: {
          advisor: Partial<Advisor>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .saveAdvisor$(action.advisor, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(managementActions.LOAD_ADVISORS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  saveInsurer$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_INSURER),
      mergeMap(
        (action: {
          insurer: Partial<Insurer>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .saveInsurer$(action.insurer, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(managementActions.LOAD_INSURERS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  loadEmails$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_EMAILS),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          { pagination?: Partial<TPagination> },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            managementActions.SET_EMAILS_LOADING_STATUS({ loading: true })
          );
          return this.managementService
            .getEmails$(action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<Email> | TEmailsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    managementActions.SET_EMAILS_LOADING_STATUS({
                      loading: false,
                    })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      managementActions.SET_MANAGEMENT_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_MANAGEMENT_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_EMAILS({
                        emailList: data,
                      })
                    );
                  }
                  return of(
                    managementActions.SET_EMAILS({ emailList: response })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_EMAILS({ emailList: [] }),
                  managementActions.SET_MANAGEMENT_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_MANAGEMENT_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_EMAILS_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadDocuments$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_DOCUMENTS),
      withLatestFrom(this.store.select(SELECT_DOCUMENTS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          { pagination?: Partial<TPagination> },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            managementActions.SET_DOCUMENTS_LOADING_STATUS({ loading: true })
          );
          return this.managementService
            .getDocuments$(action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<Document> | TDocumentsPaginated
                ): Observable<Action> => {
                  let lastActionDate = new Date();
                  this.store.dispatch(
                    managementActions.SET_DOCUMENTS_LOADING_STATUS({
                      loading: false,
                    })
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    lastActionDate = new Date(
                      Math.max(
                        ...data.map((document: Document): number =>
                          new Date(document.utcUpdateDate).getTime()
                        )
                      )
                    );
                    return of(
                      managementActions.SET_DOCUMENTS_LIST_LENGTH({
                        length: response.length,
                      }),
                      managementActions.SET_DOCUMENTS_PAGINATION({
                        pagination,
                      }),
                      managementActions.SET_DOCUMENTS({
                        documentList: response.data,
                      }),
                      managementActions.SET_LAST_ACTION_DATE({
                        date: lastActionDate,
                      })
                    );
                  }
                  lastActionDate = new Date(
                    Math.max(
                      ...response.map((document: Document): number =>
                        new Date(document.utcUpdateDate).getTime()
                      )
                    )
                  );
                  return of(
                    managementActions.SET_DOCUMENTS({ documentList: response }),
                    managementActions.SET_LAST_ACTION_DATE({
                      date: lastActionDate,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  managementActions.SET_DOCUMENTS({ documentList: [] }),
                  managementActions.SET_DOCUMENTS_LIST_LENGTH({
                    length: 0,
                  }),
                  managementActions.SET_DOCUMENTS_PAGINATION({
                    pagination: undefined,
                  }),
                  managementActions.SET_DOCUMENTS_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadCountries$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_COUNTRIES),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
        return this.managementService.getCountries$().pipe(
          switchMap((countries: Array<Country>): Observable<Action> => {
            this.store.dispatch(
              SET_MANAGEMENT_LOADING_STATUS({ loading: false })
            );
            return of(
              managementActions.SET_COUNTRIES({ countryList: countries })
            );
          }),
          catchError((): Observable<Action> => {
            return of(
              SET_MANAGEMENT_LOADING_STATUS({ loading: false }),
              managementActions.SET_COUNTRIES({ countryList: [] })
            );
          })
        );
      })
    );
  });

  saveFinancialInstitution$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_FINANCIAL_INSTITUTION),
      mergeMap(
        (action: {
          institution: Partial<FinancialInstitute>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(
            managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
              loading: true,
            })
          );
          return this.managementService
            .saveFinancialInstitution$(action.institution, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
                    loading: false,
                  })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(managementActions.LOAD_FINANCIAL_INSTITUTIONS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                return of(
                  managementActions.SET_FINANCIAL_INSTITUTIONS_LOADING({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  saveAccountManager$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_ACCOUNT_MANAGER),
      mergeMap(
        (action: {
          accountManager: Partial<AccountManager>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .saveAccountManager$(action.accountManager, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(managementActions.LOAD_ACCOUNT_MANAGERS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  saveIntermediary$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_INTERMEDIARY),
      mergeMap(
        (action: {
          intermediary: Partial<Intermediary>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .saveIntermediary$(action.intermediary, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(managementActions.LOAD_INTERMEDIARIES({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  uploadDocument$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.UPLOAD_DOCUMENT),
      mergeMap((action: { id: string; document: File }): Observable<Action> => {
        return this.managementService
          .uploadDocument$(action.id, action.document)
          .pipe(
            switchMap((): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Success,
                "DATA_SAVED_SUCCESSFULLY"
              );
              return of(
                managementActions.SET_LAST_ACTION_DATE({ date: new Date() })
              );
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DATA_COULD_NOT_BE_SAVED",
                response?.error
              );
              return of();
            })
          );
      })
    );
  });

  downloadDocument$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.DOWNLOAD_DOCUMENT),
      mergeMap(
        (action: { id: string; documentName: string }): Observable<Action> => {
          return this.managementService.getDocument$(action.id).pipe(
            switchMap((file: Blob): Observable<Action> => {
              Utilities.downloadAttachment(file, action.documentName);
              this.notificationService.show(
                ToasterSeverities.Success,
                "DATA_SAVED_SUCCESSFULLY"
              );
              return of();
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DATA_COULD_NOT_BE_SAVED",
                response?.error
              );
              return of();
            })
          );
        }
      )
    );
  });

  loadEmail$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_EMAIL),
      concatMap((action: { id: string }): Observable<Action> => {
        return this.managementService.getEmail$(action.id).pipe(
          switchMap((email: Email): Observable<Action> => {
            return of(managementActions.SET_EMAIL({ email }));
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_COULD_NOT_BE_SAVED",
              response?.error
            );
            return of();
          })
        );
      })
    );
  });

  saveEmail$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_EMAIL),
      mergeMap((action: { email: Partial<Email> }): Observable<Action> => {
        this.store.dispatch(
          managementActions.SET_EMAILS_LOADING_STATUS({ loading: true })
        );
        return this.managementService.saveEmail$(action.email).pipe(
          switchMap((): Observable<Action> => {
            this.dialog.closeAll();
            this.store.dispatch(
              managementActions.SET_EMAILS_LOADING_STATUS({ loading: false })
            );
            this.notificationService.show(
              ToasterSeverities.Success,
              "DATA_SAVED_SUCCESSFULLY"
            );
            return of(managementActions.LOAD_EMAILS({}));
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_COULD_NOT_BE_SAVED",
              response?.error
            );
            this.store.dispatch(
              managementActions.SET_EMAILS_LOADING_STATUS({ loading: false })
            );
            return of();
          })
        );
      })
    );
  });

  loadETFList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.LOAD_ETF_LIST),
      withLatestFrom(this.store.select(SELECT_MANAGEMENT_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            sorting?: TableSorting;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService
            .getETFList$(action.searchTerm ?? pagination?.searchTerm)
            .pipe(
              switchMap((etfList: Array<Etf>): Observable<Action> => {
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                if (action.searchTerm) {
                  this.store.dispatch(
                    managementActions.SET_MANAGEMENT_PAGINATION({
                      pagination: { searchTerm: action.searchTerm },
                    })
                  );
                }
                return of(managementActions.SET_ETF_LIST({ etfList }));
              }),
              catchError((): Observable<Action> => {
                this.store.dispatch(
                  SET_MANAGEMENT_LOADING_STATUS({ loading: false })
                );
                return of(managementActions.SET_ETF_LIST({ etfList: [] }));
              })
            );
        }
      )
    );
  });

  saveETF$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.SAVE_ETF),
      mergeMap(
        (action: {
          etf: Partial<Etf>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_MANAGEMENT_LOADING_STATUS({ loading: true }));
          return this.managementService.saveETF$(action.etf).pipe(
            switchMap((): Observable<Action> => {
              this.dialog.closeAll();
              this.store.dispatch(
                SET_MANAGEMENT_LOADING_STATUS({ loading: false })
              );
              this.notificationService.show(
                ToasterSeverities.Success,
                "DATA_SAVED_SUCCESSFULLY"
              );
              return of(managementActions.LOAD_ETF_LIST({}));
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DATA_COULD_NOT_BE_SAVED",
                response?.error
              );
              this.store.dispatch(
                SET_MANAGEMENT_LOADING_STATUS({ loading: false })
              );
              return of();
            })
          );
        }
      )
    );
  });

  deleteETF$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.DELETE_ETF),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.managementService.deleteETF$(action.id).pipe(
          concatMap((): Observable<Action> => {
            return of(managementActions.LOAD_ETF_LIST({}));
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  checkFundISIN$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.CHECK_FUND_ISIN),
      mergeMap((action: { isin: string }): Observable<Action> => {
        return this.managementService.checkFundIsin$(action.isin).pipe(
          switchMap((valid: boolean): Observable<Action> => {
            return of(managementActions.SET_FUND_ISIN_VALIDITY({ valid }));
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  checkForExistingEmail$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.CHECK_FOR_EXISTING_EMAIL),
      mergeMap((action: { email: string }): Observable<Action> => {
        return this.managementService.checkForExistingEmail$(action.email).pipe(
          switchMap((emailExists: boolean): Observable<Action> => {
            return of(
              managementActions.SET_EMAIL_VALIDITY({
                emailExists: emailExists,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  checkForExistingUsername$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.CHECK_FOR_EXISTING_USERNAME),
      mergeMap((action: { username: string }): Observable<Action> => {
        return this.managementService
          .checkForExistingUsername$(action.username)
          .pipe(
            switchMap((usernameExists: boolean): Observable<Action> => {
              return of(
                managementActions.SET_USERNAME_VALIDITY({
                  usernameExists: usernameExists,
                })
              );
            }),
            catchError((): Observable<Action> => {
              return of();
            })
          );
      })
    );
  });

  validateIban$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(managementActions.VALIDATE_IBAN),
      mergeMap((action: { iban: string }): Observable<Action> => {
        return this.managementService.validateIban$(action.iban).pipe(
          switchMap(
            (
              financialInstituteList: Array<FinancialInstitute>
            ): Observable<Action> => {
              if (!Array.isArray(financialInstituteList)) {
                return of(
                  managementActions.SET_FINANCIAL_INSTITUTIONS({
                    financialInstituteList: [],
                  })
                );
              }
              return of(
                managementActions.SET_FINANCIAL_INSTITUTIONS({
                  financialInstituteList,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of(
              managementActions.SET_FINANCIAL_INSTITUTIONS({
                financialInstituteList: [],
              })
            );
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private managementService: ManagementService,
    private notificationService: NotificationService,
    private store: Store,
    private dialog: DialogHelper
  ) {}
}
